import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { logout, type UserDto } from './api';
import { CURRENT_USER_KEY, useCurrentUser } from './useCurrentUser';
import { useCurrentWarehouseStore } from '../warehouses/currentWarehouse';
import { PERMISSIONS_KEY } from './usePermissions';

export interface AuthState {
  user: UserDto | null;
  isAuthenticated: boolean;
  /** True only while the initial `/me` probe is in flight. */
  isLoading: boolean;
  isSigningOut: boolean;
  signOut: () => void;
}

/**
 * Single source of truth for the session in the UI. Wraps the `/me` query
 * (TanStack Query is the store — no separate context provider needed) and the
 * logout call, so every consumer (Home header, idle-timeout, future route
 * guards) shares one definition of "who is signed in" and one sign-out path.
 */
export function useAuth(): AuthState {
  const query = useCurrentUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const signOutMutation = useMutation({
    mutationFn: () => logout(),
    onSuccess: async () => {
      queryClient.setQueryData(CURRENT_USER_KEY, null);
      await queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY });
      // Dropped rather than invalidated: the next user to sign in on this device must not see
      // the previous one's menu for even one render while a refetch is in flight.
      queryClient.removeQueries({ queryKey: PERMISSIONS_KEY });
      // The warehouse belonged to that session, not to this device. These are shared handhelds:
      // without this the next person to sign in starts in the last person's warehouse, and if they
      // happen to reach it too, nothing tells them they are in the wrong building.
      useCurrentWarehouseStore.getState().setWarehouse(null);
      notifications.show({ message: t('auth.signOutSuccess'), color: 'green' });
      void navigate({ to: '/login' });
    },
  });

  // `mutate` is a stable reference from TanStack Query, so `signOut` is stable
  // across renders — safe to use in effect dependency lists (idle-timeout).
  const { mutate } = signOutMutation;
  const signOut = useCallback(() => mutate(), [mutate]);

  return {
    user: query.data ?? null,
    isAuthenticated: Boolean(query.data),
    isLoading: query.isLoading,
    isSigningOut: signOutMutation.isPending,
    signOut,
  };
}
